package br.com.texeratx.boardgametimeapp.ui.screens.players.create

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.core.image.PickedImage
import br.com.texeratx.boardgametimeapp.data.model.player.CreatePlayerRequest
import br.com.texeratx.boardgametimeapp.domain.usecases.player.CreatePlayerUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.upload.UploadPlayerImageUseCase
import br.com.texeratx.boardgametimeapp.ui.utils.uiToApiDate
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class CreatePlayerViewModel(
    private val createPlayerUseCase: CreatePlayerUseCase,
    private val uploadPlayerImageUseCase: UploadPlayerImageUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    val state = MutableStateFlow(CreatePlayerState())
    val uiMessage = MutableStateFlow<String?>(null)

    private var pendingImage: PickedImage? = null

    fun updateState(reducer: (CreatePlayerState) -> CreatePlayerState) {
        state.value = reducer(state.value)
    }

    fun onImageSelected(image: PickedImage) {
        pendingImage = image
        updateState {
            it.copy(
                selectedImageName = image.fileName,
                localImageBytes = image.bytes,
                localImageMimeType = image.mimeType
            )
        }
    }

    fun clearLocalImage() {
        pendingImage = null
        updateState {
            it.copy(
                selectedImageName = null,
                localImageBytes = null,
                localImageMimeType = null
            )
        }
    }

    fun createPlayer(onSuccess: () -> Unit) {
        screenModelScope.launch {
            val s = state.value

            if (s.name.isBlank()) {
                updateState { it.copy(error = lang.strings.value.playerNameRequired) }
                return@launch
            }

            val apiBirthday = uiToApiDate(s.birthday)
            if (s.birthday.isNotBlank() && apiBirthday == null) {
                updateState { it.copy(error = lang.strings.value.invalidBirthday) }
                return@launch
            }

            updateState { it.copy(isSaving = true, error = null) }

            // ✅ 1) upload (se houver imagem pendente)
            var finalImageUrl: String? = s.imageUrl.ifBlank { null }
            val pending = pendingImage

            if (pending != null) {
                updateState { it.copy(isUploadingImage = true) }

                val uploadResult = uploadPlayerImageUseCase(
                    bytes = pending.bytes,
                    fileName = pending.fileName,
                    mimeType = pending.mimeType
                )

                uploadResult.fold(
                    onSuccess = { url ->
                        finalImageUrl = url
                        updateState { it.copy(imageUrl = url, isUploadingImage = false) }
                    },
                    onFailure = { e ->
                        updateState {
                            it.copy(
                                isUploadingImage = false,
                                isSaving = false,
                                error = e.message ?: lang.strings.value.errorUploadingImage
                            )
                        }
                        return@launch
                    }
                )
            }

            // ✅ 2) cria player com URL final
            val result = createPlayerUseCase(
                CreatePlayerRequest(
                    name = s.name,
                    nickname = s.nickname.ifBlank { null },
                    imageUrl = finalImageUrl,
                    birthday = apiBirthday,
                    phone = s.phone.ifBlank { null },
                    status = 1
                )
            )

            result.fold(
                onSuccess = {
                    uiMessage.value = lang.strings.value.playerCreatedSuccess
                    updateState { CreatePlayerState() }
                    pendingImage = null
                    onSuccess()
                },
                onFailure = { e ->
                    updateState { it.copy(isSaving = false, error = e.message ?: lang.strings.value.errorCreatingPlayer) }
                }
            )
        }
    }
}
